/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment } from 'react'

import { EuiHealth, EuiLink, EuiSpacer, EuiTableRowCell, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { CuiTableColumn } from '@modules/cui/Table'
import { CuiHelpTipIcon } from '@modules/cui/HelpTipIcon'
import CuiElasticConsumptionUnits from '@modules/cui/formatters/CuiElasticConsumptionUnits'
import type { ProjectInstance } from '@modules/billing-api/customTypes'
import { colorForInstances } from '@modules/billing-lib/colorGenerator'

import BillingUsageTable from '@/components/Billing/BillingUsage/BillingUsageOverviewV2/BillingUsageTable'

import AggregationTableRowV2 from '../components/AggregationTableRowV2'
import { ColorKeyForOtherProjects, VisibleInstances } from '../constants'

import { getRowColor, getTotalProjectCollapsedCosts, getTotalCost } from './utils'

import type { ProjectSolution, TotalProjectCollapsedCosts } from './types'
import type { FunctionComponent } from 'react'

interface Props {
  instances: ProjectInstance[]
  isAggregated: boolean
  isLoading: boolean
  onSelect: (id: string, name?: string) => void
  organizationId: string
}

const ProjectsList: FunctionComponent<Props> = ({
  instances,
  isAggregated,
  isLoading,
  onSelect,
  organizationId,
}) => {
  const items = instances.sort((a, b) => b.total_ecu - a.total_ecu)
  const totalCost = getTotalCost(items)
  const visibleItems = items.slice(0, VisibleInstances)
  const collapsedItems = items.slice(VisibleInstances)
  const collapsedTotals = getTotalProjectCollapsedCosts(collapsedItems)

  const getSolutionName = (solution: ProjectSolution) =>
    ({
      elasticsearch: (
        <FormattedMessage
          id='billing-usage-project-list.search-type'
          defaultMessage='Elasticsearch'
        />
      ),
      observability: (
        <FormattedMessage
          id='billing-usage-project-list.observability-type'
          defaultMessage='Observability'
        />
      ),
      security: (
        <FormattedMessage id='billing-usage-project-list.security-type' defaultMessage='Security' />
      ),
    })[solution]

  const renderGroupedSolutions = (solutions: ProjectSolution[]) =>
    solutions.map((solution, i) => [i > 0 && ', ', getSolutionName(solution)])

  const columns: Array<CuiTableColumn<ProjectInstance>> = [
    {
      label: <FormattedMessage id='billing-usage-project-list.name-label' defaultMessage='Name' />,
      render: ({ name, id }) => (
        <EuiHealth
          color={isAggregated ? colorForInstances.get(ColorKeyForOtherProjects) : getRowColor(id)}
        >
          <EuiLink disabled={!id} onClick={() => onSelect(id, name)}>
            <span data-test-id='name'>{name || id}</span>
          </EuiLink>

          {name && (
            <Fragment>
              <EuiSpacer size='xs' />
              <EuiText color='subdued' size='xs'>
                {id}
              </EuiText>
            </Fragment>
          )}
        </EuiHealth>
      ),
      width: '60%',
      footer: {
        render: () => (
          <Fragment>
            <FormattedMessage id='billing-usage-project-list.total' defaultMessage='Total usage' />
            <CuiHelpTipIcon iconType='info'>
              <FormattedMessage
                id='billing-deployment-usage.tip-icon'
                defaultMessage='Usage only. Does not include credits, prepaids, or any other discounts.'
              />
            </CuiHelpTipIcon>
          </Fragment>
        ),
      },
    },
    {
      label: (
        <FormattedMessage
          id='billing-usage-project-list.solution-label'
          defaultMessage='Solution'
        />
      ),
      render: ({ type }) => getSolutionName(type),
      footer: {
        render: () => null,
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage
          id='billing-usage-project-list.total-label'
          defaultMessage='Total (in ECU)'
        />
      ),
      render: ({ total_ecu }) => (
        <CuiElasticConsumptionUnits value={total_ecu} unit='none' dp={4} withSymbol={false} />
      ),
      footer: {
        render: () => (
          <CuiElasticConsumptionUnits
            value={totalCost}
            unit='none'
            dp={4}
            withSymbol={false}
            symbolPosition='before'
          />
        ),
      },
    },
    {
      actions: true, // additional actions column
      width: '40px',
    },
  ]

  return (
    <BillingUsageTable<ProjectInstance>
      title={
        <FormattedMessage
          id='billing-project-usage-list.title'
          defaultMessage='Serverless projects'
        />
      }
      rows={visibleItems}
      columns={columns}
      isLoading={isLoading}
      organizationId={organizationId}
      dataTestId='projects-list-usage-table'
      renderCustomRowsLast={() => (
        <AggregationTableRowV2<TotalProjectCollapsedCosts, ProjectInstance>
          onItemSelected={onSelect}
          totals={collapsedTotals}
          items={collapsedItems}
          type='project'
          renderRow={({ total_ecu, type }) => (
            <Fragment>
              <EuiTableRowCell>
                {Array.isArray(type) ? renderGroupedSolutions(type) : getSolutionName(type)}
              </EuiTableRowCell>
              <EuiTableRowCell align='right'>
                <CuiElasticConsumptionUnits
                  value={total_ecu}
                  unit='none'
                  dp={4}
                  withSymbol={false}
                />
              </EuiTableRowCell>
              <EuiTableRowCell className='euiTableCellContent' />
            </Fragment>
          )}
        />
      )}
    />
  )
}

export default ProjectsList
